import {
  getHomePageTaskSections,
  isFrictionlessSignupUser,
  shouldDisplayPaywallOnHomeScreen,
} from '@frontend/api-client';
import { dayjs, getRandomElementsFromArray } from '@frontend/duck-tape';
import { validators } from '@frontend/forms';
import {
  GIFT_A_TASK_COPY_DEFAULT_VALUE,
  useEffect,
  useFeatureIsOn,
  useFeatureValue,
  useGapFilledFeatureValue,
  useMemo,
  useState,
  useVisibilityController,
} from '@frontend/react';
import { useLowBandwidthBanner, useLowCreditBonusBanner } from '@frontend/react-domain/credits';
import { Button, PageContainer, XStack, YStack, isMobile } from '@frontend/web-react';
import { applyClass, markOnboardingCompleted, safeCss, setOnboardingStep } from '@frontend/web-utils';
import {
  HeaderButton,
  HeadsUpBanner,
  HomeCreateTaskInput,
  HomeTasksContainer,
  InviteFriends,
  OnboardingTaskCard,
  PersonalizeContainer,
  ProfileWizardCard,
  Suggestions,
  TypedText,
} from '@frontend/web/components';
import {
  LowBandwidthHomeBanner,
  LowCreditBonusBanner,
  ReactivationModal,
  SubscriptionBanner,
} from '@frontend/web/components/DomainSpecific/Account';
import { GiftBanner, GiftTaskModal } from '@frontend/web/components/DomainSpecific/Home/Gift';
import { PaywallModal } from '@frontend/web/components/DomainSpecific/Paywall';
import {
  useAccessControl,
  useEditProfileMutation,
  useListDraftTasksQuery,
  useListTasksQuery,
  useReactivationFlow,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
  useRetrieveCreditsQuery,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { spotlight } from '@mantine/spotlight';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

const ReferAFriendButton = () => {
  const navigate = useNavigate();
  return (
    <Button
      leftIconName="IconHeartHandshake"
      onClick={() => navigate({ to: '/app/settings/invite-friends' })}
      size="sm"
      variant="secondary"
    >
      Refer a friend
    </Button>
  );
};

const emptyTextOptions = [
  'what can we help with?',
  "what's on your mind?",
  'what do you need done?',
  'tell me what you need',
  "what's next on your list?",
];

const Home = () => {
  const {
    data: tasks = [],
    isLoading: isListTasksLoading,
    refetch: refetchTasks,
  } = useListTasksQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const {
    data: draftTasks = [],
    isLoading: isDraftTasksLoading,
    refetch: refetchDraftTasks,
  } = useListDraftTasksQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { isAccessControlLoading, isClientSubscribed } = useAccessControl();
  const [updateClient] = useEditProfileMutation();
  const { value: markAsDoneDeployDate } = useFeatureValue('mark_as_done_deploy_date');
  const { enabled: isHomeCreateTaskInputEnabled } = useFeatureIsOn('is_home_create_task_input_enabled');
  const [isPaywallModalOpen, { close: closePaywallModal, open: openPaywallModal }] = useVisibilityController();
  const [isGiftModalOpen, { close: closeGiftModal, open: openGiftModal }] = useVisibilityController();
  const {
    value: { banner: giftBannerCopy, modal: giftModalCopy },
  } = useGapFilledFeatureValue('gift_a_task_copy', GIFT_A_TASK_COPY_DEFAULT_VALUE);
  const {
    closeReactivationModal,
    handleReactivation,
    hasReactivationError,
    isChurned,
    isReactivating,
    isReactivationModalOpen,
  } = useReactivationFlow();
  const { data: clientStatus } = useRetrieveClientStatusQuery();
  const { data: client } = useRetrieveClientQuery();
  const canGiftATask = client?.capabilities?.some((c) => c.capability === 'gift_a_task') ?? false;
  const [hasUserDismissedPaywall, setHasUserDismissedPaywall] = useState(false);
  const { data: credits } = useRetrieveCreditsQuery();
  const { dismiss: dismissBanner, shouldShowBanner: shouldShowLowBandwidthBanner } = useLowBandwidthBanner(credits);
  const [isLowCreditBonusBannerDismissed, setIsLowCreditBonusBannerDismissed] = useState(false);
  const { shouldShowBanner: isEligibleForLowCreditBonus } = useLowCreditBonusBanner(client);
  const shouldShowLowCreditBonusBanner = isEligibleForLowCreditBonus && !isLowCreditBonusBannerDismissed;
  const ageOfUserInDays = client?.createdAt ? dayjs().diff(dayjs(client.createdAt), 'day') : 0;

  const { last7daysDraftTasks, latestTasks } = useMemo(
    () =>
      getHomePageTaskSections({
        draftTasks,
        markAsDoneDeployDate,
        tasks,
      }),
    [tasks, draftTasks, markAsDoneDeployDate],
  );

  useEffect(() => {
    if (isChurned || hasUserDismissedPaywall || !client?.birthday || !client?.phoneNumber) {
      return;
    }

    if (shouldDisplayPaywallOnHomeScreen(client, clientStatus) && !isPaywallModalOpen) {
      setOnboardingStep('web_paywall');
      openPaywallModal();
    }
  }, [client, clientStatus, isChurned, isPaywallModalOpen, hasUserDismissedPaywall, openPaywallModal]);

  const handleDismissPaywall = () => {
    setHasUserDismissedPaywall(true);
    closePaywallModal();
  };

  useEffect(() => {
    const handleFocus = () => {
      refetchTasks();
      refetchDraftTasks();
    };
    window.addEventListener('focus', handleFocus);
    handleFocus();
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetchDraftTasks, refetchTasks]);

  const onPaywallSuccess = () => {
    handleMutation({
      mutation: () => updateClient({ onboardingComplete: true }).unwrap(),
      onSuccess: async () => {
        handleDismissPaywall();
        markOnboardingCompleted();
      },
      showSuccessToast: false,
    });
  };

  const isLoading = isListTasksLoading || isDraftTasksLoading;

  const isEmpty = !latestTasks.length && !isLoading;

  const hasTasks = latestTasks.length > 0;

  const randomGreeting = useMemo(
    () => getRandomElementsFromArray(emptyTextOptions, 1)[0] ?? (emptyTextOptions[0] as string),
    [],
  );

  const dynamicTitle = useMemo(() => {
    if (!client?.firstName) return null;

    if (isHomeCreateTaskInputEnabled) {
      return (
        <TypedText
          fixedText={`Hi ${client.firstName},`}
          key="home-title"
          playOnce
          showCursor={false}
          strings={[randomGreeting]}
          type="h1"
          typeSpeed={30}
        />
      );
    }

    return (
      <TypedText
        fixedText={!hasTasks ? `Hi ${client.firstName},` : undefined}
        key={hasTasks ? 'has-tasks' : 'no-tasks'}
        loop={!hasTasks}
        playOnce={hasTasks}
        showCursor={!hasTasks}
        startDelay={500}
        strings={hasTasks ? [`Hi ${client.firstName}, here's what we're working on...`] : emptyTextOptions}
        type="h2"
        typeSpeed={30}
      />
    );
  }, [client?.firstName, hasTasks, isHomeCreateTaskInputEnabled, randomGreeting]);

  const handleSearchClick = () => {
    if (isMobile) {
      setTimeout(() => spotlight.open(), 50);
    } else {
      spotlight.open();
    }
  };

  const tailSections = (
    <>
      {!isEmpty ? (
        <YStack className="gap-xl">
          <HomeTasksContainer seeAll taskLikeObjects={latestTasks} title="In progress" />
          <HomeTasksContainer taskLikeObjects={last7daysDraftTasks} title="Drafts" />
        </YStack>
      ) : null}
      {canGiftATask ? (
        <YStack className="pt-lg">
          <GiftBanner
            heading={giftBannerCopy.heading}
            label={giftBannerCopy.label}
            onOpen={openGiftModal}
            subheading={giftBannerCopy.subheading}
          />
        </YStack>
      ) : null}
      <YStack gap="lg" pt="lg">
        <div className="fade-in-up-delay-1">
          <Suggestions />
        </div>
        <div className="fade-in-up-delay-2">
          <div className="pb-lg">
            <ProfileWizardCard />
          </div>
          <PersonalizeContainer />
        </div>
        <div className="fade-in-up-delay-3">
          <InviteFriends />
        </div>
      </YStack>
    </>
  );

  return (
    <>
      <PageContainer
        HeaderComponent={client?.headsUpBanner ? <HeadsUpBanner message={client.headsUpBanner} /> : null}
        contentContainerClassName={safeCss(
          `${shouldShowLowBandwidthBanner || shouldShowLowCreditBonusBanner ? 'pb-xxxl' : 'pb-md'} flex-1 overflow-y-scroll`,
        )}
        countSkeletons={16}
        isLoading={isLoading}
        isScrollable
        padding="none"
      >
        <YStack
          className={applyClass(
            isHomeCreateTaskInputEnabled,
            'w-full items-center py-xxl',
            'w-full items-center pt-sm lg:pt-lg pb-xxl',
          )}
        >
          {isHomeCreateTaskInputEnabled ? (
            <>
              <XStack className="hidden md:flex max-w-screen-lg w-full justify-end px-lg xl:px-none pt-md">
                <ReferAFriendButton />
              </XStack>
              <YStack className="max-w-screen-lg w-full px-lg xl:px-none pt-xxxl gap-lg">
                <YStack className={applyClass(!isClientSubscribed && !isAccessControlLoading, 'py-md')}>
                  <YStack gapY="md">
                    {!isClientSubscribed && !isAccessControlLoading ? <SubscriptionBanner /> : null}
                  </YStack>
                </YStack>
                <YStack className="items-center gap-xxl pb-xxl w-full">
                  {!isListTasksLoading && dynamicTitle}
                  <HomeCreateTaskInput />
                </YStack>
                <OnboardingTaskCard ageOfUserInDays={ageOfUserInDays} completedCount={tasks.length} />
                {tailSections}
              </YStack>
            </>
          ) : (
            <YStack className="max-w-screen-lg w-full px-lg xl:px-none">
              <XStack className="gap-md justify-between items-center py-lg">
                <YStack className="flex-1">{!isListTasksLoading && dynamicTitle}</YStack>
                <XStack className="gap-md items-center">
                  <XStack className="hidden md:flex">
                    <ReferAFriendButton />
                  </XStack>
                  <HeaderButton
                    accessibilityLabel="Search"
                    color="primary"
                    iconName="IconSearch"
                    onClick={handleSearchClick}
                  />
                </XStack>
              </XStack>
              <YStack className="py-md">
                <YStack gapY="md">
                  {!isClientSubscribed && !isAccessControlLoading ? <SubscriptionBanner /> : null}
                </YStack>
              </YStack>
              <YStack pb="lg">
                <OnboardingTaskCard ageOfUserInDays={ageOfUserInDays} completedCount={tasks.length} />
              </YStack>
              {tailSections}
            </YStack>
          )}
        </YStack>
        <LowBandwidthHomeBanner
          dismiss={dismissBanner}
          shouldShowBanner={Boolean(shouldShowLowBandwidthBanner && !shouldShowLowCreditBonusBanner)}
        />
        <LowCreditBonusBanner
          dismiss={() => setIsLowCreditBonusBannerDismissed(true)}
          shouldShowBanner={shouldShowLowCreditBonusBanner}
        />
      </PageContainer>
      <PaywallModal
        isFrictionlessUser={isFrictionlessSignupUser(client)}
        isOpen={isPaywallModalOpen}
        onClose={handleDismissPaywall}
        onSuccess={onPaywallSuccess}
        shouldCompleteOnboardingOnClose
      />
      <ReactivationModal
        hasError={hasReactivationError}
        isLoading={isReactivating}
        isOpen={isReactivationModalOpen}
        onClose={closeReactivationModal}
        onContinue={handleReactivation}
      />
      {canGiftATask ? (
        <GiftTaskModal
          benefits={giftModalCopy.benefits}
          ctaLabel={giftModalCopy.cta_label}
          heading={giftModalCopy.heading}
          isOpen={isGiftModalOpen}
          onClose={closeGiftModal}
          subheading={giftModalCopy.subheading}
        />
      ) : null}
    </>
  );
};

export const homeValidator = validators.object({
  description: validators.encodedURI().optional(),
  name: validators.encodedURI().optional(),
  newTask: validators.boolean().optional(),
});

export const Route = createFileRoute('/app/home')({
  component: Home,
});
